import { Alert, Button } from '@mm/components';
import {
  MAX_ALLOWED_BTNS,
  MAX_ALLOWED_QQ_TEXT_LENGTH,
} from '@mm/constants/widgets';
import { useSafeTimeout } from '@mm/hooks';
import { PlusCircleIcon } from '@mm/icons';
import ButtonModel from '@mm/state/model/ButtonModel';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import classnames from 'classnames';
import { cloneDeep } from 'lodash';
import React, {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Draggable, DraggableProvided, DropResult } from 'react-beautiful-dnd';
import { colors } from '../../../../editor/features/beta/styles/colors';
import { TYPES } from '../../../../editor/features/build/components/widgets/constants/quickQuestion';
import { WidgetContainerRefProp } from '../../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { MMdndDroppable } from '../../../components/MMdndDroppable/MMdndDroppable';
import { PrimaryToolTip } from '../../../components/PrimaryToolTip/PrimaryToolTip';
import { TooltipContainer } from '../../../components/TooltipContainer/TooltipContainer';
import {
  getQQUserResponseType,
  useQQWidgetHandlers,
} from '../../../helpers/widget-helpers/use-qq-widget-handlers';
import {
  BubbleTextInput,
  InputFieldRefType,
} from '../../common/BubbleTextInput/BubbleTextInput';
import { FacebookPageIcon } from '../../common/FacebookPageIcon/FacebookPageIcon';
import { PathGraphContext } from '../../common/PathGraph/PathGraph';
import { WidgetControls } from '../../common/WidetControls/WidgetControls';
import './QuestionBotTextArea.pcss';
import { QuestionButton } from './QuestionButton/QuestionButton';

const sortButtons = (button1: ButtonModel, button2: ButtonModel) =>
  button1.sortOrder - button2.sortOrder;

type Props = {
  quickQuestion: WidgetModel;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  questionInputHotSpotRef: MutableRefObject<HTMLDivElement | null>;
  selectedButton: number | undefined;
  setSelectedButton: (selectedButton: number | undefined) => void;
  disableDragging?: boolean;
  disableDeleting?: boolean;
  disableConditionalLogic?: boolean;
  canEditQuestion: boolean;
  canEditAnswers: boolean;
  isSmsOptIn: boolean;
  draggableProvided?: DraggableProvided;
  showAttributePicker?: boolean;
  showAttributesStartWarning?: boolean;
  showOnlyNameAttributes?: boolean;
  dialogueId?: number;
  showCountByChannel?: boolean;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

const QuestionBotTextArea = (props: Props) => {
  const {
    quickQuestion,
    editMode,
    setEditMode,
    questionInputHotSpotRef,
    selectedButton,
    setSelectedButton,
    disableDragging,
    disableDeleting,
    disableConditionalLogic,
    canEditQuestion,
    canEditAnswers,
    draggableProvided,
    showAttributePicker = true,
    showOnlyNameAttributes = false,
    showAttributesStartWarning = false,
    dialogueId,
    isSmsOptIn,
    showCountByChannel,
    platforms,
  } = props;
  const { safeTimeout } = useSafeTimeout();
  const qqWidgetHandlers = useQQWidgetHandlers();
  const qqResponseType = getQQUserResponseType(quickQuestion);
  const canAddButton = quickQuestion.buttons.length < MAX_ALLOWED_BTNS;
  const [addAnswerSaving, setAddAnswerSaving] = useState(false);
  const [updatingButtons, setUpdatingButtons] = useState(false);
  const [randomSpecificityId, setRandomSpecificityId] = useState(
    Math.floor(Math.random() * 1000000),
  );

  const {
    setActiveNode,
    removeNodeAndSuccessors,
    getActiveButtonFromWidgetButtons,
  } = useContext(PathGraphContext);

  const beforeDestroy = () => {
    const activeButton = getActiveButtonFromWidgetButtons?.(
      quickQuestion.buttons,
    );
    if (activeButton) {
      setActiveNode?.({
        id: activeButton.id,
        type: 'button',
        action: 'close',
      });
    }
    removeNodeAndSuccessors?.({
      id: quickQuestion.id,
      type: 'widget',
      actionType: 'delete',
      parentId: dialogueId,
      parentType: 'dialogue',
    });
  };

  const onClickAddAnswer = async () => {
    setAddAnswerSaving(true);

    beforeDestroy();

    safeTimeout(async () => {
      const newButton = await qqWidgetHandlers.addPostBackButton(quickQuestion);

      if (newButton) {
        setSelectedButton(newButton.id);
        setActiveNode?.({
          id: newButton.id,
          type: 'button',
          action: 'open',
          // Node: no dialogueId here, since initially the type of the button is postback
        });
      }

      setAddAnswerSaving(false);
    }, 500);
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) return;
    if (source.index === destination.index) return;

    setUpdatingButtons(true);
    const buttons = quickQuestion.buttons.sort(sortButtons);
    const nextButtons = cloneDeep(buttons);
    const { index: sourceIndex } = source;
    const { index: destinationIndex } = destination;

    nextButtons.splice(sourceIndex, 1);
    nextButtons.splice(destinationIndex, 0, buttons[sourceIndex]);

    const updatedButtons = nextButtons.map((button, index) => ({
      ...button,
      sortOrder: index + 1,
    }));

    setTimeout(async () => {
      await qqWidgetHandlers.updateButtons(quickQuestion, updatedButtons);
      setUpdatingButtons(false);
    }, 15);
  };

  const inputFieldRef = useRef() as InputFieldRefType;
  const onQuestionInputClick = () => {
    // hackery due to input component design
    setTimeout(() => inputFieldRef?.current?.focus(), 300);
    setEditMode(true);
  };

  const [extraTextLength, setExtraTextLength] = useState(0);
  const bodyLength = quickQuestion.config.body?.length ?? 0;
  const smsStructureLength = quickQuestion.smsStructure.body?.length ?? 0;
  const smsStructure = quickQuestion.smsStructure.body;

  const [hasAttributesInBody, setHasAttributesInBody] = useState(false);

  useEffect(() => {
    if (quickQuestion.config.body) {
      setHasAttributesInBody(/{{.+}}/.test(quickQuestion.config.body));
    }
  }, [quickQuestion.config.body]);

  useEffect(() => {
    setExtraTextLength(smsStructureLength - bodyLength);
  }, [smsStructureLength]);

  return (
    <div
      className={classnames('question-bot-text-area', {
        'question-bot-text-area--edit-mode': editMode,
        'question-bot-text-area--no-button-selected': !selectedButton,
      })}
    >
      <div className="question-bot-text-area__main-area-wrapper">
        <FacebookPageIcon />

        <div className="question-bot-text-area__right-area">
          <div className="question-bot-text-area__right-top-area">
            <BubbleTextInput
              ref={questionInputHotSpotRef}
              updateText={newText =>
                qqWidgetHandlers.updateQuestionText(quickQuestion, newText)
              }
              inputFieldRefToSet={inputFieldRef}
              value={quickQuestion.config.body}
              disabled={!canEditQuestion}
              editMode={editMode}
              setEditMode={setEditMode}
              onClick={onQuestionInputClick}
              showAttributePicker={showAttributePicker}
              showOnlyNameAttributes={showOnlyNameAttributes}
              newCharacterCountDesign
              showCountByChannel={showCountByChannel}
              maxSmsChannelTextLength={MAX_ALLOWED_QQ_TEXT_LENGTH}
              maxOthersChannelTextLength={MAX_ALLOWED_QQ_TEXT_LENGTH}
              extraTextLength={extraTextLength}
              hasQQButtons={!!quickQuestion.buttons.length}
              smsStructure={smsStructure}
            />
            {isSmsOptIn && (
              <div className="question-bot-text-area__tooltip-container">
                <TooltipContainer
                  classNameContent="question-bot-text-area__tooltip"
                  classNameAnchor="question-bot-text-area__tooltip-anchor"
                  classNamePopoverContainer="question-bot-text-area__tooltip-popover"
                  message="Question text should ask user if they would like to receive SMS messages"
                />
              </div>
            )}

            <WidgetControls
              widget={quickQuestion}
              beforeDestroy={beforeDestroy}
              draggableProvided={draggableProvided}
              disableDragging={disableDragging}
              disableDeleting={disableDeleting}
              disableConditionalLogic={disableConditionalLogic}
              platforms={platforms}
            />

            {!showAttributePicker &&
              hasAttributesInBody &&
              showAttributesStartWarning && (
                <div className="question-widget__alert-container">
                  <Alert
                    className="question-widget__warning"
                    type={'error'}
                    showCloseIcon={false}
                  >
                    Attributes are not available on the first widget since we
                    only get contact’s profile AFTER their first response.
                  </Alert>
                </div>
              )}
          </div>

          {qqResponseType === TYPES.MULTIPLE && (
            <div className="question-bot-text-area__question-buttons">
              <MMdndDroppable
                droppableId={`question-button__droppable-${quickQuestion.id}-${randomSpecificityId}`}
                direction="horizontal"
                type={`question-button__droppable-${quickQuestion.id}`}
                onDragEnd={onDragEnd}
              >
                {provided => (
                  <div
                    className={classnames(
                      'question-bot-text-area__question-buttons-container',
                      {
                        'question-bot-text-area__question-buttons-container--disabled':
                          updatingButtons,
                      },
                    )}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {quickQuestion.buttons
                      .sort(sortButtons)
                      .map((button, index) => (
                        <Draggable
                          key={button.id}
                          draggableId={`${button.id}`}
                          index={index}
                          disableInteractiveElementBlocking={true}
                        >
                          {(draggableProvided, snapshot) => (
                            <QuestionButton
                              editMode={
                                canEditAnswers && selectedButton === button.id
                              }
                              smsOptinMode={isSmsOptIn && index < 2}
                              setSelectedButton={setSelectedButton}
                              button={button}
                              quickQuestion={quickQuestion}
                              key={button.id}
                              draggableProvided={draggableProvided}
                              isDragging={snapshot.isDragging}
                              dialogueId={dialogueId}
                            />
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </MMdndDroppable>

              {canEditAnswers && (
                <PrimaryToolTip
                  align="center"
                  position="bottom"
                  anchorElement={
                    <Button
                      color="secondary-blue"
                      size="sm"
                      leftIcon={<PlusCircleIcon color={colors.accentBlue} />}
                      disabled={!canAddButton || addAnswerSaving}
                      css={{
                        container: classnames(
                          'question-bot-text-area__add-new-button',
                        ),
                      }}
                      onClick={onClickAddAnswer}
                    >
                      Add
                    </Button>
                  }
                  useHover={true}
                  classNameContainer="question-bot-text-area__tool-tip-area"
                  toolTipDisabled={canAddButton}
                >
                  Reached limit of {MAX_ALLOWED_BTNS} options
                </PrimaryToolTip>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionBotTextArea;
